/** @jsxImportSource react */
import { useRef, useState } from 'react'
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Paper, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { documentTemplatesAPI, recordsAPI } from '../../api/crm'
import { useEntityTypes, usePublishedForm, useTenantKey } from '../../hooks/useCrmQueries'
import { allFields } from '../../components/records/fieldValues'
import { getErrorMessage } from '../../utils/apiErrors'
import { entityLabel, formatDate } from '../../utils/helpers'
import { useNotify } from '../../components/common/NotificationProvider'
import PageHeader from '../../components/common/PageHeader'
import Loading from '../../components/common/Loading'
import EmptyState from '../../components/common/EmptyState'
import ConfirmDialog from '../../components/common/ConfirmDialog'
import PdfPreview from '../../components/documents/PdfPreview'
import { blobError } from '../../components/documents/RecordDocuments'

const SPECIAL = [
  { key: 'record.title', label: 'Record name' },
  { key: 'today', label: 'Today' },
  { key: 'me.name', label: 'My name' },
  { key: 'me.email', label: 'My email' },
  { key: 'workspace.name', label: 'Workspace' },
]

const EXAMPLE = `<h1>Sanction letter</h1>
<p class="muted">{{today}} · Ref. {{record.title}}</p>

<p>Dear {{applicant}},</p>
<p>We are pleased to inform you that your loan application has been <b>sanctioned</b> on the terms below.</p>

<table>
  <tr><th style="width:40%">Loan amount</th><td>₹{{amount}}</td></tr>
  <tr><th>Tenure</th><td>{{tenure}} months</td></tr>
  <tr><th>Interest rate</th><td>{{rate}}% per year</td></tr>
</table>

<h3>Charges</h3>
{{table:FEE}}

<p>Please sign this letter to accept the offer.</p>
<p style="margin-top:40px">For {{workspace.name}}<br/><b>{{me.name}}</b></p>
`

/** Admins: PDF layouts filled in from records (sanction letters, quotes, agreements). */
const DocumentTemplatesPage = () => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const [editing, setEditing] = useState(null)
  const [deleting, setDeleting] = useState(null)
  const list = useQuery({ queryKey: [tenantId, 'document-templates', 'all'], queryFn: () => documentTemplatesAPI.list() })
  const remove = useMutation({
    mutationFn: (id) => documentTemplatesAPI.remove(id),
    onSuccess: () => { notify('Template deleted'); setDeleting(null); queryClient.invalidateQueries({ queryKey: [tenantId, 'document-templates'] }) },
  })
  if (list.isLoading) return <Loading />
  if (list.error) return <Alert severity="error">{getErrorMessage(list.error)}</Alert>
  return (
    <Box sx={{ maxWidth: 1000 }}>
      <PageHeader title="Document templates"
        subtitle="PDFs made from a record in one click: sanction letters, quotes, agreements. They can then be emailed or sent for e-signature."
        actions={<Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing({})}>New template</Button>} />
      {list.data.length === 0 ? (
        <Paper variant="outlined"><EmptyState title="No document templates yet" description="Start from the example sanction letter and adapt it." /></Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead><TableRow><TableCell>Name</TableCell><TableCell>For</TableCell><TableCell>File name</TableCell><TableCell>Changed</TableCell><TableCell /></TableRow></TableHead>
            <TableBody>
              {list.data.map((t) => (
                <TableRow key={t.id} hover>
                  <TableCell sx={{ fontWeight: 500 }}>{t.name}</TableCell>
                  <TableCell>{t.entityType ? entityLabel(t.entityType) : 'Any record'}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{t.fileName}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>{formatDate(t.updatedOn)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <IconButton size="small" aria-label={`Edit ${t.name}`} onClick={() => setEditing(t)}><EditOutlinedIcon fontSize="small" /></IconButton>
                    <IconButton size="small" aria-label={`Delete ${t.name}`} onClick={() => setDeleting(t)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {editing && <TemplateEditor template={editing} onClose={() => setEditing(null)} />}
      <ConfirmDialog open={!!deleting} title={`Delete “${deleting?.name}”?`} message="Documents already made from it stay in their records."
        confirmLabel="Delete" destructive busy={remove.isPending} onConfirm={() => remove.mutate(deleting.id)} onClose={() => setDeleting(null)} />
    </Box>
  )
}

const TemplateEditor = ({ template, onClose }) => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const fullScreen = useMediaQuery(useTheme().breakpoints.down('md'))
  const { entityTypes, forms } = useEntityTypes()
  const bodyRef = useRef(null)
  const [form, setForm] = useState({
    name: template.name || '', entityType: template.entityType || '', fileName: template.fileName || '{{record.title}}', bodyHtml: template.bodyHtml || '',
  })
  const [recordId, setRecordId] = useState('')
  const [preview, setPreview] = useState(null)
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }))
  const formCode = forms.find((f) => f.entityType === form.entityType)?.code
  const { data: published } = usePublishedForm(formCode)
  const fields = allFields(published)
  const childTypes = [...new Set(forms.filter((f) => f.entityType !== form.entityType).map((f) => f.entityType))]
  const records = useQuery({
    queryKey: [tenantId, 'records', 'pick', form.entityType],
    queryFn: () => recordsAPI.list({ entityType: form.entityType, size: 20 }),
    enabled: !!form.entityType,
  })

  const insert = (text) => {
    const el = bodyRef.current
    const start = el?.selectionStart ?? form.bodyHtml.length
    const end = el?.selectionEnd ?? start
    setForm((f) => ({ ...f, bodyHtml: f.bodyHtml.slice(0, start) + text + f.bodyHtml.slice(end) }))
    setTimeout(() => { el?.focus(); el?.setSelectionRange(start + text.length, start + text.length) }, 0)
  }
  const body = { ...form, entityType: form.entityType || null }
  const save = useMutation({
    mutationFn: () => (template.id ? documentTemplatesAPI.update(template.id, body) : documentTemplatesAPI.create(body)),
    onSuccess: () => { notify('Template saved'); queryClient.invalidateQueries({ queryKey: [tenantId, 'document-templates'] }); onClose() },
  })
  const run = useMutation({ mutationFn: () => documentTemplatesAPI.previewDraft(recordId, body), onSuccess: setPreview })

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth fullScreen={fullScreen}
      PaperProps={{ component: 'form', onSubmit: (e) => { e.preventDefault(); e.stopPropagation(); save.mutate() } }}>
      <DialogTitle>{template.id ? `Edit ${template.name}` : 'New document template'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.1fr) minmax(0, 1fr)' }, gap: 3, pt: 1 }}>
          <Stack spacing={2}>
            {save.error && <Alert severity="error">{getErrorMessage(save.error)}</Alert>}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField fullWidth required label="Name" value={form.name} onChange={set('name')} inputProps={{ maxLength: 100 }} />
              <TextField fullWidth select label="For records of type" value={form.entityType} onChange={set('entityType')}>
                <MenuItem value="">Any type</MenuItem>
                {entityTypes.map((t) => <MenuItem key={t} value={t}>{entityLabel(t)}</MenuItem>)}
              </TextField>
            </Stack>
            <TextField required label="PDF file name" value={form.fileName} onChange={set('fileName')} inputProps={{ maxLength: 200 }}
              helperText="Placeholders work here too, e.g. Sanction letter - {{record.title}}" />
            <Box>
              <Typography variant="caption" color="text.secondary">Click to insert at the cursor:</Typography>
              <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75} sx={{ mt: 0.5 }}>
                {SPECIAL.map((p) => <Chip key={p.key} size="small" label={p.label} onClick={() => insert(`{{${p.key}}}`)} />)}
                {fields.map((f) => <Chip key={f.fieldKey} size="small" variant="outlined" label={f.label} onClick={() => insert(`{{${f.fieldKey}}}`)} />)}
                {form.entityType && childTypes.map((t) => (
                  <Tooltip key={t} title={`A table of the ${entityLabel(t).toLowerCase()} records linked to this one (e.g. line items)`}>
                    <Chip size="small" variant="outlined" color="primary" label={`Table: ${entityLabel(t)}`} onClick={() => insert(`{{table:${t}}}`)} />
                  </Tooltip>
                ))}
              </Stack>
            </Box>
            <TextField required multiline minRows={14} maxRows={28} label="Layout (HTML)" value={form.bodyHtml} onChange={set('bodyHtml')}
              inputRef={bodyRef} InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
              helperText="Headings, paragraphs, tables, bold, colours, images embedded as data: URIs. Classes: right, center, muted, page-break." />
            {!form.bodyHtml && <Box><Button size="small" onClick={() => setForm((f) => ({ ...f, bodyHtml: EXAMPLE }))}>Start from an example sanction letter</Button></Box>}
          </Stack>
          <Stack spacing={1.5}>
            <Typography variant="subtitle2">Preview</Typography>
            {!form.entityType ? (
              <Typography variant="body2" color="text.secondary">Choose a record type to preview with one of its records.</Typography>
            ) : (
              <Stack direction="row" spacing={1}>
                <TextField select size="small" fullWidth label="With the record" value={recordId} onChange={(e) => setRecordId(e.target.value)}>
                  {(records.data?.items || []).map((r) => <MenuItem key={r.id} value={r.id}>{r.title}</MenuItem>)}
                </TextField>
                <Button variant="outlined" disabled={!recordId || !form.bodyHtml || run.isPending} onClick={() => run.mutate()}>
                  {run.isPending ? '…' : 'Preview'}
                </Button>
              </Stack>
            )}
            {run.error && <Alert severity="error">{blobError(run.error)}</Alert>}
            <PdfPreview blob={preview} height={620} />
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={save.isPending}>Save</Button>
      </DialogActions>
    </Dialog>
  )
}

export default DocumentTemplatesPage
